import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function PhotosPage() {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingType, setUploadingType] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [selfieStatus, setSelfieStatus] = useState(null);

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      setCurrentUser(response.data);
      setPhotos(response.data.photos || []);
      setSelfieStatus(response.data.selfie_verified);
    } catch (err) {
      navigate('/login');
    }
  };

  const handlePhotoUpload = async (e, type = 'profile') => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      alert('يرجى اختيار صورة بصيغة JPEG أو PNG');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('حجم الصورة كبير جداً. الحد الأقصى 5MB');
      return;
    }

    setUploading(true);
    setUploadingType(type);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const endpoint = type === 'selfie' ? '/api/upload/selfie' : '/api/upload/photo';
      const response = await axios.post(
        `${BACKEND_URL}${endpoint}`,
        formData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      if (response.data.success) {
        if (type === 'profile') {
          setPhotos([...photos, response.data.photo_url]);
          alert('✅ تم رفع الصورة بنجاح! سيتم مراجعتها من قبل الإدارة');
        } else {
          setSelfieStatus('pending');
          alert('✅ تم رفع صورة التحقق! سيتم مراجعتها وإضافة شارة التحقق لحسابك');
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'فشل رفع الصورة';
      alert(`❌ ${errorMsg}`);
    } finally {
      setUploading(false);
      setUploadingType(null);
    }
  };

  const removePhoto = async (photoUrl, index) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الصورة؟')) return;
    
    try {
      // For now, just remove from local state
      // In production, you'd call an API to delete
      const newPhotos = photos.filter((_, i) => i !== index);
      setPhotos(newPhotos);
      
      // Update user photos in backend
      await axios.post(
        `${BACKEND_URL}/api/profile/complete`,
        { photos: newPhotos },
        { withCredentials: true }
      );
      
      alert('✅ تم حذف الصورة');
    } catch (err) {
      alert('❌ فشل حذف الصورة');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--light-bg)' }}>
      {/* Header */}
      <div className="header">
        <div className="header-content">
          <h1 className="logo" onClick={() => navigate('/dashboard')} style={{cursor: 'pointer'}}>💍 وِداد</h1>
          <div className="nav-menu">
            <button className="nav-link" onClick={() => navigate('/dashboard')}>الرئيسية</button>
            <button className="nav-link" onClick={() => navigate('/search')}>البحث</button>
            <button className="nav-link" onClick={() => navigate('/messages')}>الرسائل</button>
            <button className="nav-link" onClick={() => navigate('/pricing')}>الباقات</button>
          </div>
        </div>
      </div>

      <div className="container" style={{ paddingTop: '40px', maxWidth: '800px' }}>
        <h2 style={{ color: 'var(--primary)', marginBottom: '8px', fontSize: '28px' }}>
          📷 صوري الشخصية
        </h2>
        <p style={{ color: 'var(--text-light)', marginBottom: '32px' }}>
          أضف حتى 3 صور شخصية. الصور تظهر فقط للأشخاص الذين قبلت طلب تواصلهم
        </p>

        {/* Profile Photos Section */}
        <div className="card" style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '20px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🖼️</span>
            <span>صور الملف الشخصي</span>
            <span style={{ 
              fontSize: '14px', 
              color: 'var(--text-light)', 
              fontWeight: 'normal',
              marginRight: 'auto'
            }}>
              ({photos.length}/3)
            </span>
          </h3>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', 
            gap: '16px',
            marginBottom: '20px'
          }}>
            {/* Existing Photos */}
            {photos.map((photo, index) => (
              <div 
                key={index}
                style={{ 
                  position: 'relative',
                  paddingTop: '100%',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  background: '#f0f0f0'
                }}
              >
                <img 
                  src={photo.startsWith('http') ? photo : `${BACKEND_URL}${photo}`}
                  alt={`صورة ${index + 1}`}
                  style={{ 
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                <button
                  onClick={() => removePhoto(photo, index)}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    left: '8px',
                    background: 'rgba(255, 87, 87, 0.9)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  ✕
                </button>
                <div style={{
                  position: 'absolute',
                  bottom: '8px',
                  right: '8px',
                  background: 'rgba(0,0,0,0.6)',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '11px'
                }}>
                  ⏳ قيد المراجعة
                </div>
              </div>
            ))}

            {/* Add Photo Button */}
            {photos.length < 3 && (
              <label 
                style={{ 
                  paddingTop: '100%',
                  position: 'relative',
                  borderRadius: '12px',
                  border: '3px dashed var(--border-color)',
                  background: 'var(--card-bg)',
                  cursor: uploading && uploadingType === 'profile' ? 'wait' : 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center'
                }}>
                  {uploading && uploadingType === 'profile' ? (
                    <>
                      <div className="spinner" style={{ margin: '0 auto 8px' }}></div>
                      <p style={{ fontSize: '13px', color: 'var(--text-light)' }}>جاري الرفع...</p>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: '36px', display: 'block', marginBottom: '8px' }}>➕</span>
                      <p style={{ fontSize: '13px', color: 'var(--text-light)' }}>إضافة صورة</p>
                    </>
                  )}
                </div>
                <input 
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => handlePhotoUpload(e, 'profile')}
                  style={{ display: 'none' }}
                  disabled={uploading}
                />
              </label>
            )}
          </div>

          <div style={{ 
            background: 'var(--card-bg)', 
            border: '1px solid var(--border-color)',
            padding: '12px 16px', 
            borderRadius: '8px',
            fontSize: '13px',
            color: 'var(--text-dark)'
          }}>
            💡 <strong>نصيحة:</strong> الصور الواضحة والمحترمة تزيد من فرص التواصل. تجنب الصور الجماعية أو المفلترة بشكل مبالغ.
          </div>
        </div>

        {/* Selfie Verification Section */}
        <div className="card">
          <h3 style={{ fontSize: '20px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>✅</span>
            <span>التحقق من الهوية</span>
            {selfieStatus === true && (
              <span className="badge badge-success" style={{ marginRight: 'auto' }}>
                ✓ موثق
              </span>
            )}
          </h3>

          <p style={{ color: 'var(--text-light)', marginBottom: '20px', lineHeight: '1.7' }}>
            ارفع صورة سيلفي واضحة لوجهك للحصول على شارة "حساب موثق" 🏅
            <br/>
            <span style={{ fontSize: '13px' }}>هذا يزيد من ثقة الآخرين بك ويعطيك يوم مجاني من العضوية المميزة!</span>
          </p>

          {selfieStatus === true ? (
            <div style={{ 
              background: 'rgba(0, 217, 165, 0.1)', 
              padding: '20px', 
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <span style={{ fontSize: '48px', display: 'block', marginBottom: '12px' }}>🏅</span>
              <h4 style={{ color: 'var(--success)', marginBottom: '8px' }}>حسابك موثق!</h4>
              <p style={{ color: 'var(--text-light)', fontSize: '14px' }}>
                تم التحقق من هويتك بنجاح
              </p>
            </div>
          ) : selfieStatus === 'pending' ? (
            <div style={{ 
              background: 'rgba(254, 193, 99, 0.1)', 
              padding: '20px', 
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <span style={{ fontSize: '48px', display: 'block', marginBottom: '12px' }}>⏳</span>
              <h4 style={{ color: 'var(--secondary)', marginBottom: '8px' }}>قيد المراجعة</h4>
              <p style={{ color: 'var(--text-light)', fontSize: '14px' }}>
                تم استلام صورتك وسيتم مراجعتها قريباً
              </p>
            </div>
          ) : (
            <label style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              padding: '24px',
              border: '3px dashed var(--primary)',
              borderRadius: '12px',
              cursor: uploading && uploadingType === 'selfie' ? 'wait' : 'pointer',
              background: 'rgba(255, 107, 157, 0.05)',
              transition: 'all 0.2s'
            }}>
              {uploading && uploadingType === 'selfie' ? (
                <>
                  <div className="spinner"></div>
                  <span style={{ color: 'var(--text-light)' }}>جاري الرفع...</span>
                </>
              ) : (
                <>
                  <span style={{ fontSize: '32px' }}>🤳</span>
                  <div>
                    <p style={{ fontWeight: '600', color: 'var(--primary)', marginBottom: '4px' }}>
                      رفع صورة سيلفي للتحقق
                    </p>
                    <p style={{ fontSize: '13px', color: 'var(--text-light)' }}>
                      صورة واضحة لوجهك من الأمام
                    </p>
                  </div>
                </>
              )}
              <input 
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => handlePhotoUpload(e, 'selfie')}
                style={{ display: 'none' }}
                disabled={uploading}
              />
            </label>
          )}
        </div>

        {/* Back Button */}
        <button 
          className="btn btn-outline btn-full" 
          style={{ marginTop: '24px' }}
          onClick={() => navigate('/dashboard')}
        >
          ← العودة للرئيسية
        </button>
      </div>
    </div>
  );
}

export default PhotosPage;
